<template>
  <a-modal
    :open="visible"
    width="80%"
    :footer="null"
    :mask-closable="false"
    class="ai-thinking-modal"
    @cancel="handleClose"
  >
    <!-- 自定义标题 -->
    <template #title>
      <div class="w-full flex items-center justify-between">
        <div class="flex items-center">
          <icon-vue icon="mdi:brain" class="mr-2 text-purple-600" font-size="20px" />
          <h3 class="text-lg font-medium text-gray-800">完整思考过程</h3>
          <span v-if="tokenStats" class="ml-3 rounded bg-green-100 px-2 py-1 text-xs text-green-700">
            {{ tokenStats }} Tokens
          </span>
        </div>
      </div>
    </template>

    <!-- 思考内容 -->
    <div ref="thinkingContentRef" class="thinking-content-container" @scroll="handleScroll">
      <div v-if="!hasContent" class="empty-content py-16 text-center">
        <icon-vue icon="mdi:brain" class="mb-3 text-gray-300" font-size="64px" />
        <p class="text-base text-gray-400">暂无思考内容</p>
      </div>

      <div v-else class="thinking-content">
        <pre class="m-0 whitespace-pre-wrap p-0 leading-relaxed text-gray-800">{{ content }}</pre>

        <!-- 滚动提示 -->
        <div
          v-if="isUserScrolling && isContentUpdating"
          class="sticky bottom-2 left-0 right-0 mt-2 flex justify-center"
        >
          <div class="scroll-to-bottom-btn" @click="clickScrollToBottom">
            <icon-vue icon="mdi:arrow-down" font-size="14px" />
            <span>新内容正在生成，点击跳转到底部</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div class="thinking-footer mt-6 flex items-center justify-between border-t bg-gray-50 px-6 pb-4 pt-4 -mx-6 -mb-6">
      <div class="text-sm text-gray-500">
        <span>分析时长: {{ duration }}</span>
      </div>
      <div class="flex space-x-3">
        <a-button size="middle" :icon="h(CopyOutlined)" :loading="copyLoading" class="copy-btn" @click="handleCopy">
          {{ copyLoading ? '复制中...' : '复制' }}
        </a-button>
        <a-button
          type="primary"
          size="middle"
          :icon="h(DownloadOutlined)"
          :loading="downloadLoading"
          class="download-btn"
          @click="handleDownload"
        >
          {{ downloadLoading ? '下载中...' : '下载' }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons-vue';
import { Icon as IconVue } from '@iconify/vue';
import { message } from 'ant-design-vue';
import { computed, h, nextTick, ref, watch } from 'vue';

// Props
interface Props {
  visible: boolean;
  content: string;
  duration?: string;
  tokenStats?: string;
  chunkCount?: number;
  isContentUpdating?: boolean; // 新增：内容是否正在更新中
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  content: '',
  duration: '--',
  tokenStats: '',
  chunkCount: 0,
  isContentUpdating: false,
});

// Emits
const emit = defineEmits(['update:visible', 'close']);

// State
const copyLoading = ref(false);
const downloadLoading = ref(false);

// 滚动控制状态
const thinkingContentRef = ref<HTMLElement>();
const isUserScrolling = ref(false);
const autoScrollEnabled = ref(true);

// Computed
const hasContent = computed(() => !!props.content);

// Methods
function handleClose() {
  emit('update:visible', false);
  emit('close');
}

// 滚动控制函数
const scrollToBottom = () => {
  if (thinkingContentRef.value && autoScrollEnabled.value) {
    nextTick(() => {
      thinkingContentRef.value!.scrollTop = thinkingContentRef.value!.scrollHeight;
    });
  }
};

// 点击跳转到底部
const clickScrollToBottom = () => {
  if (thinkingContentRef.value) {
    nextTick(() => {
      thinkingContentRef.value!.scrollTop = thinkingContentRef.value!.scrollHeight;
      // 重新启用自动滚动
      autoScrollEnabled.value = true;
      isUserScrolling.value = false;
    });
  }
};

// 处理用户滚动
const handleScroll = () => {
  if (!thinkingContentRef.value) {
    return;
  }

  const { scrollTop, scrollHeight, clientHeight } = thinkingContentRef.value;
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px容差

  // 如果用户滚动到底部，重新启用自动滚动
  if (isAtBottom) {
    autoScrollEnabled.value = true;
    isUserScrolling.value = false;
  } else {
    // 用户手动滚动到其他位置，禁用自动滚动
    autoScrollEnabled.value = false;
    isUserScrolling.value = true;
  }
};

async function handleCopy() {
  if (!props.content) {
    message.warning('暂无思考内容可复制');
    return;
  }

  copyLoading.value = true;
  try {
    await navigator.clipboard.writeText(props.content);
    message.success('思考内容已复制到剪贴板');
  } catch (err) {
    console.error('复制失败:', err);
    message.error('复制失败，请手动选择并复制');
  } finally {
    copyLoading.value = false;
  }
}

function handleDownload() {
  if (!props.content) {
    message.warning('暂无思考内容可下载');
    return;
  }

  downloadLoading.value = true;
  try {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const duration = props.duration || '未知';

    const fileContent = `AI精准测试分析 - 思考过程记录
==========================================

分析时间: ${new Date().toLocaleString()}
分析时长: ${duration}
数据块数: ${props.chunkCount || 0}
内容长度: ${props.content.length} 字符
Token统计: ${props.tokenStats || '未统计'}

==========================================
完整思考过程:
==========================================

${props.content}

==========================================
生成时间: ${new Date().toLocaleString()}
生成工具: AI精准测试模块
==========================================`;

    const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AI思考过程_${timestamp}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    message.success('思考过程已下载');
  } catch (err) {
    console.error('下载失败:', err);
    message.error('下载失败，请稍后重试');
  } finally {
    downloadLoading.value = false;
  }
}

// 监听思考内容变化，实现SSE流式输出效果
watch(
  () => props.content,
  (newContent, oldContent) => {
    // 只有在弹窗可见、内容更新中且内容增加时才自动滚动
    if (props.visible && props.isContentUpdating && newContent && newContent.length > (oldContent?.length || 0)) {
      scrollToBottom();
    }
  },
  { flush: 'post' },
);

// 监听弹窗显示状态，重置滚动状态
watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      // 弹窗打开时重置滚动状态
      autoScrollEnabled.value = true;
      isUserScrolling.value = false;
      // 如果有内容，滚动到底部
      if (props.content) {
        nextTick(() => {
          scrollToBottom();
        });
      }
    }
  },
);
</script>

<style scoped>
.ai-thinking-modal :deep(.ant-modal-content) {
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
}

.ai-thinking-modal :deep(.ant-modal-header) {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  padding: 16px 24px;
  margin: 0;
}

.ai-thinking-modal :deep(.ant-modal-title) {
  font-size: 18px;
  font-weight: 500;
  color: #111827;
}

.ai-thinking-modal :deep(.ant-modal-body) {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 24px 0;
}

.thinking-content-container {
  flex: 1;
  overflow-y: auto;
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-size: 13px;
  line-height: 1.6;
  min-height: 400px;
  max-height: 60vh;
}

.thinking-content-container::-webkit-scrollbar {
  width: 8px;
}

.thinking-content-container::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.thinking-content-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.thinking-content-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.empty-content {
  color: #9ca3af;
}

.thinking-content {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.thinking-footer {
  margin-top: auto;
  flex-shrink: 0;
}

/* 按钮样式优化 */
.copy-btn {
  border-color: #d1d5db;
  color: #374151;
  background: #ffffff;
  transition: all 0.2s ease;
}

.copy-btn:hover {
  border-color: #9ca3af;
  color: #111827;
  background: #f9fafb;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.download-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border: none;
  transition: all 0.2s ease;
}

.download-btn:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

/* 滚动到底部按钮样式 */
.scroll-to-bottom-btn {
  display: inline-flex;
  cursor: pointer;
  align-items: center;
  gap: 0.5rem;
  border-radius: 9999px;
  background-color: #3b82f6;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  color: white;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: background-color 0.2s;
}

.scroll-to-bottom-btn:hover {
  background-color: #2563eb;
}
</style>
